import type { StudentAnswerInput, StudentTheoryAnswerInput } from '@/types/composition-functions';

/** Seconds before the end at which the student gets a one-off warning toast. */
export const TIME_WARNING_THRESHOLDS_SECONDS = [300, 60] as const;

export const SUBMIT_FAILED_MESSAGE =
  'We couldn\'t submit your exam. Your answers are still saved. Check your internet connection and tap Submit again.';

export const SAVE_PENDING_MESSAGE =
  'Some answers haven\'t saved yet — we\'ll keep retrying. Don\'t close this page.';

/**
 * The exam deadline is always the session start plus the test duration. It must
 * never be derived from the latest auto-saved answer: that moves forward on every
 * save and would hand the student extra time after a page refresh.
 */
export function computeExamDeadline(startTime: string | null | undefined, durationMinutes: number | string | null | undefined): number {
  const start = startTime ? new Date(startTime).getTime() : NaN;
  const minutes = Number(durationMinutes) || 0;
  if (Number.isNaN(start)) return 0;
  return start + minutes * 60 * 1000;
}

/** Whole seconds left before the deadline, never negative. */
export function secondsRemaining(deadline: number, now: number = Date.now()): number {
  const diff = Math.floor((deadline - now) / 1000);
  return diff > 0 ? diff : 0;
}

/**
 * Which warning (if any) should fire now. Each threshold fires once: pass the
 * thresholds already shown, and the caller records the returned one.
 */
export function dueTimeWarning(secondsLeft: number, alreadyShown: ReadonlySet<number>): number | null {
  if (secondsLeft <= 0) return null;
  // Smallest threshold first, so a page opened with 45s left shows the 1-minute warning, not the 5-minute one.
  const sorted = [...TIME_WARNING_THRESHOLDS_SECONDS].sort((a, b) => a - b);
  for (const threshold of sorted) {
    if (secondsLeft <= threshold && !alreadyShown.has(threshold)) return threshold;
  }
  return null;
}

export function timeWarningMessage(threshold: number): string {
  const minutes = Math.round(threshold / 60);
  const unit = minutes === 1 ? 'minute' : 'minutes';
  return `${minutes} ${unit} left — your answers will be submitted automatically when time runs out.`;
}

export function isAnswered(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export interface AnswerSummary {
  answered: number;
  total: number;
  unanswered: number;
}

export function summarizeAnswers(questionIds: string[], answers: Record<string, unknown>): AnswerSummary {
  const total = questionIds.length;
  const answered = questionIds.filter((id) => isAnswered(answers[id])).length;
  return { answered, total, unanswered: total - answered };
}

export function submitConfirmationMessage(summary: AnswerSummary): string {
  const { answered, total, unanswered } = summary;
  const base = `You have answered ${answered} of ${total} question${total === 1 ? '' : 's'}.`;
  if (unanswered === 0) return `${base} Submit your exam now?`;
  return `${base} ${unanswered} ${unanswered === 1 ? 'is' : 'are'} unanswered. Submit anyway?`;
}

/**
 * Put answers whose save failed back in front of the queue. A newer entry already
 * queued for the same question wins, so a retry never overwrites a later edit.
 */
export function requeueTheoryAnswers(
  failed: StudentTheoryAnswerInput[],
  queued: StudentTheoryAnswerInput[],
): StudentTheoryAnswerInput[] {
  const newer = new Set(queued.map((a) => a.question_id));
  return [...failed.filter((a) => !newer.has(a.question_id)), ...queued];
}

/** Objective answers are re-derived from the live answer state on flush, so only the question ids matter. */
export function requeueObjectiveAnswers(
  failed: StudentAnswerInput[],
  queued: StudentAnswerInput[],
): StudentAnswerInput[] {
  const newer = new Set(queued.map((a) => a.question_id));
  return [...failed.filter((a) => !newer.has(a.question_id)), ...queued];
}

type GraphQLErrorShape = { message?: string; extensions?: { validation?: Record<string, unknown> } };

/**
 * A human reason for a failed submit, when the server gave one in
 * extensions.validation (e.g. "This test has already been submitted or has
 * expired."); otherwise the reassuring default. Raw transport text is never shown.
 */
export function submitErrorMessage(error: unknown): string {
  const gqlErrors = (error as { graphQLErrors?: GraphQLErrorShape[] } | null)?.graphQLErrors ?? [];
  for (const gqlError of gqlErrors) {
    const validation = gqlError?.extensions?.validation;
    if (!validation) continue;
    for (const messages of Object.values(validation)) {
      const first = Array.isArray(messages) ? messages[0] : messages;
      if (typeof first === 'string' && first.trim()) return first;
    }
  }
  return SUBMIT_FAILED_MESSAGE;
}
